import {CustomProperty} from 'common/types/reports/query';
import {applyPartialUpdates} from 'common/util/model-resource/api-resource';
import {ModelStatus} from 'common/util/model-resource/model-resource';

import {CustomPropertiesResource} from '../model-resources/custom-properties';

/**
 * A shared place to cache inline/unsaved custom properties
 * Implemented as a class so it can be used by Panel components.
 * Separated from the CustomProperties resource because
 *  1) The items are not saved to the API
 *  2) To reduce the number of dependent components
 *  3) Model resources cause dependents to re-render when they change; we don't want that TODO: confirm this
 * */
export class InlineCustomPropertiesStore {
  protected inlineCustomProperties: {[key: string]: CustomProperty} = {};

  public set(value: {[key: string]: CustomProperty}) {
    this.inlineCustomProperties = value;
  }

  public get() {
    return this.inlineCustomProperties;
  }

  public update(updates: {[key: string]: CustomProperty}) {
    for (const [id, update] of Object.entries(updates)) {
      if (id in this.inlineCustomProperties) {
        this.inlineCustomProperties[id] = applyPartialUpdates(this.inlineCustomProperties[id], update);
      } else {
        this.inlineCustomProperties[id] = update;
      }
    }
  }

  public remove(customPropertyId: number) {
    if (customPropertyId in this.inlineCustomProperties) {
      delete this.inlineCustomProperties[customPropertyId];
    }
  }

  /**
   * Layers inline/unsaved custom properties onto persisted ones, so they can be treated as a homogeneous list.
   * @param customProperties CustomPropertiesResource with persisted custom props
   * @returns A combined map with the inline custom properties included
   */
  public layerOnto(customProperties: CustomPropertiesResource) {
    const allCustomProps = Array.from(customProperties.getAll().entries())
      .filter((entry) => entry[1].status === ModelStatus.Success && !!entry[1].value)
      .reduce((acc, curr) => {
        acc.set(curr[0], curr[1].value!);
        return acc;
      }, new Map<string, CustomProperty>());

    for (const [id, inline] of Object.entries(this.inlineCustomProperties)) {
      if (allCustomProps.has(id)) {
        allCustomProps.set(id, applyPartialUpdates(allCustomProps.get(id), inline));
      } else {
        allCustomProps.set(id, inline);
      }
    }

    return allCustomProps;
  }
}
